import {Injectable} from '@angular/core';
import {Router, UrlTree} from '@angular/router';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthService} from '../auth/auth.service';
import {Principal} from '../auth/principal.service';
import {UserRole} from '../auth/user-role.model';

/**
 * Empty-path (`''`) redirect by role.
 * ADVERTISER / FINANCE land on `/reports` instead of `/dashboard` (SRS-47),
 * avoiding blocked GET /api/dashboard/** calls that leave the UI spinning.
 * All other roles keep the previous default: `/dashboard`.
 */
@Injectable()
export class LandingRedirectGuard {

  constructor(
    private authService: AuthService,
    private principal: Principal,
    private router: Router,
  ) {
  }

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.identity().pipe(
      map(() => {
        this.principal.ensureDefaultMask();

        const roles = this.principal.getUser()?.roles ?? [];
        const isAdvertiserOrFinance =
          roles.includes(UserRole.ADVERTISER) || roles.includes(UserRole.FINANCE);
        const hasHigherPriority = roles.some(role => [
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.READ_ONLY_ADMIN,
          // USER/READONLY_USER are what the backend actually issues now (fold of
          // CAMPAIGN_MANAGER/DESIGNER and SALES_REPRESENTATIVE/REV_OPS respectively - see
          // user-role.model.ts); the legacy roles are never present in a real user's roles[].
          UserRole.USER,
          UserRole.READONLY_USER,
        ].includes(role as UserRole));

        if (isAdvertiserOrFinance && !hasHigherPriority) {
          if (!this.principal.mask?.role) {
            this.principal.setMask(
              roles.includes(UserRole.ADVERTISER) ? UserRole.ADVERTISER : UserRole.FINANCE
            );
          }
          return this.router.parseUrl('/reports');
        }

        // Preserve legacy empty-path behavior for Admin / CM / SR / etc.
        return this.router.parseUrl('/dashboard');
      })
    );
  }
}
